/**
 * Slide Category Utilities & Definitions
 * Provides section categories (Chorus, Verse, Bridge, Intro, Outro, Tag),
 * badge styling, keyboard shortcuts, auto-detection from Lyric JSON / HTML metadata,
 * and quick-jump navigation helpers.
 */

export const SLIDE_CATEGORIES = [
  {
    id: 'intro',
    shortcutKey: 'i',
    labelKey: 'presentation.catIntro',
    defaultLabel: 'Intro',
    shortLabel: 'INT',
    color: 'slate',
    hexColor: '#64748b',
    dotClass: 'bg-slate-500',
    leftBorderClass: 'border-l-slate-500',
    badgeClass: 'bg-slate-700 text-slate-100 border-slate-600',
    chipClass: 'bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',
    pillClass: 'bg-slate-100 text-slate-700 border-slate-300 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700 dark:hover:bg-slate-700',
    activePillClass: 'bg-slate-800 text-white border-slate-800 shadow-sm dark:bg-slate-200 dark:text-slate-900 dark:border-white',
    keywords: ['intro', 'introducao', 'introdução', 'entrada', 'q-intro'],
    patterns: [/^\s*\[?(?:intro(?:du[cç][aã]o)?|entrada)\]?/i],
  },
  {
    id: 'verse',
    shortcutKey: 'v',
    labelKey: 'presentation.catVerse',
    defaultLabel: 'Verso',
    shortLabel: 'V',
    color: 'blue',
    hexColor: '#3b82f6',
    dotClass: 'bg-blue-500',
    leftBorderClass: 'border-l-blue-500',
    badgeClass: 'bg-blue-600 text-blue-100 border-blue-500',
    chipClass: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/60 dark:text-blue-300 dark:border-blue-800',
    pillClass: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 dark:bg-blue-950/60 dark:text-blue-300 dark:border-blue-800 dark:hover:bg-blue-900/60',
    activePillClass: 'bg-blue-600 text-white border-blue-600 shadow-sm dark:bg-blue-500 dark:text-white dark:border-blue-400',
    keywords: ['verse', 'verso', 'estrofe', 'strophe', 'q-verse'],
    patterns: [/^\s*\[?(?:verso|verse|estrofe|strophe)(?:\s*\d+)?\]?/i, /^\s*\[?v\d+\]?/i],
  },
  {
    id: 'pre_chorus',
    shortcutKey: 'p',
    labelKey: 'presentation.catPreChorus',
    defaultLabel: 'Pré-Refrão',
    shortLabel: 'PRÉ',
    color: 'amber',
    hexColor: '#f59e0b',
    dotClass: 'bg-amber-500',
    leftBorderClass: 'border-l-amber-500',
    badgeClass: 'bg-amber-600 text-amber-100 border-amber-500',
    chipClass: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/60 dark:text-amber-300 dark:border-amber-800',
    pillClass: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100 dark:bg-amber-950/60 dark:text-amber-300 dark:border-amber-800 dark:hover:bg-amber-900/60',
    activePillClass: 'bg-amber-600 text-white border-amber-600 shadow-sm dark:bg-amber-500 dark:text-white dark:border-amber-400',
    keywords: ['pre_chorus', 'pre-chorus', 'prechorus', 'prelude', 'pre_refrão', 'pre-refrão', 'q-prelude', 'q-pre_chorus'],
    patterns: [/^\s*\[?(?:pr[eé][- ]?refr[aã]o|pre[- ]?chorus|prechorus|prelude)\]?/i],
  },
  {
    id: 'chorus',
    shortcutKey: 'c',
    labelKey: 'presentation.catChorus',
    defaultLabel: 'Refrão',
    shortLabel: 'REF',
    color: 'emerald',
    hexColor: '#10b981',
    dotClass: 'bg-emerald-500',
    leftBorderClass: 'border-l-emerald-500',
    badgeClass: 'bg-emerald-600 text-emerald-100 border-emerald-500 font-black',
    chipClass: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-800 font-semibold',
    pillClass: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-800 dark:hover:bg-emerald-900/60',
    activePillClass: 'bg-emerald-600 text-white border-emerald-600 shadow-sm dark:bg-emerald-500 dark:text-white dark:border-emerald-400',
    keywords: ['chorus', 'refrao', 'refrão', 'coro', 'q-chorus'],
    patterns: [/^\s*\[?(?:refr[aã]o|chorus|coro)\]?/i],
  },
  {
    id: 'bridge',
    shortcutKey: 'b',
    labelKey: 'presentation.catBridge',
    defaultLabel: 'Ponte',
    shortLabel: 'PONTE',
    color: 'purple',
    hexColor: '#a855f7',
    dotClass: 'bg-purple-500',
    leftBorderClass: 'border-l-purple-500',
    badgeClass: 'bg-purple-600 text-purple-100 border-purple-500',
    chipClass: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/60 dark:text-purple-300 dark:border-purple-800',
    pillClass: 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100 dark:bg-purple-950/60 dark:text-purple-300 dark:border-purple-800 dark:hover:bg-purple-900/60',
    activePillClass: 'bg-purple-600 text-white border-purple-600 shadow-sm dark:bg-purple-500 dark:text-white dark:border-purple-400',
    keywords: ['bridge', 'ponte', 'coda', 'q-bridge'],
    patterns: [/^\s*\[?(?:ponte|bridge|coda)\]?/i],
  },
  {
    id: 'tag',
    shortcutKey: 't',
    labelKey: 'presentation.catTag',
    defaultLabel: 'Tag / Interlúdio',
    shortLabel: 'TAG',
    color: 'cyan',
    hexColor: '#06b6d4',
    dotClass: 'bg-cyan-500',
    leftBorderClass: 'border-l-cyan-500',
    badgeClass: 'bg-cyan-600 text-cyan-100 border-cyan-500',
    chipClass: 'bg-cyan-50 text-cyan-700 border-cyan-200 dark:bg-cyan-950/60 dark:text-cyan-300 dark:border-cyan-800',
    pillClass: 'bg-cyan-50 text-cyan-700 border-cyan-200 hover:bg-cyan-100 dark:bg-cyan-950/60 dark:text-cyan-300 dark:border-cyan-800 dark:hover:bg-cyan-900/60',
    activePillClass: 'bg-cyan-600 text-white border-cyan-600 shadow-sm dark:bg-cyan-500 dark:text-white dark:border-cyan-400',
    keywords: ['tag', 'interlude', 'interludio', 'interlúdio', 'solo', 'arrange', 'instrumental', 'q-tag', 'q-solo', 'q-arrange'],
    patterns: [/^\s*\[?(?:tag|interl[uú]dio|interlude|instrumental|solo|arrange)\]?/i],
  },
  {
    id: 'outro',
    shortcutKey: 'o',
    labelKey: 'presentation.catOutro',
    defaultLabel: 'Final',
    shortLabel: 'FIM',
    color: 'rose',
    hexColor: '#f43f5e',
    dotClass: 'bg-rose-500',
    leftBorderClass: 'border-l-rose-500',
    badgeClass: 'bg-rose-600 text-rose-100 border-rose-500',
    chipClass: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/60 dark:text-rose-300 dark:border-rose-800',
    pillClass: 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100 dark:bg-rose-950/60 dark:text-rose-300 dark:border-rose-800 dark:hover:bg-rose-900/60',
    activePillClass: 'bg-rose-600 text-white border-rose-600 shadow-sm dark:bg-rose-500 dark:text-white dark:border-rose-400',
    keywords: ['outro', 'finishing', 'final', 'ending', 'termino', 'término', 'q-outro', 'q-finishing'],
    patterns: [/^\s*\[?(?:final|outro|ending|t[eé]rmino|finishing)\]?/i],
  },
];

/**
 * Returns configuration object for a category ID
 */
export function getCategoryConfig(categoryId) {
  if (!categoryId) return null;
  return SLIDE_CATEGORIES.find((c) => c.id === categoryId) || null;
}

/**
 * Returns configuration object matching a keyboard shortcut key (e.g. 'c', 'v', 'b')
 */
export function getCategoryByShortcut(key) {
  if (!key) return null;
  const lower = String(key).toLowerCase();
  return SLIDE_CATEGORIES.find((c) => c.shortcutKey === lower) || null;
}

/**
 * Matches a keyword string (from data-type, section, type) to a category ID.
 */
function matchCategoryFromKeyword(keyword) {
  if (!keyword || typeof keyword !== 'string') return null;
  // Numbered sections ("verse 1", "verse-2") share their category: Studio emits data-type="verse 1".
  const clean = keyword.toLowerCase().trim().replace(/^q-/, '').replace(/[\s_-]+\d+$/, '');
  for (const cat of SLIDE_CATEGORIES) {
    if (cat.id === clean || cat.keywords.some((k) => k.replace(/^q-/, '') === clean)) {
      return cat.id;
    }
  }
  return null;
}

/**
 * Matches a class list string (from LyricNode className or HTML class attribute) to a category ID.
 */
function matchCategoryFromClasses(classList) {
  if (!classList || typeof classList !== 'string') return null;
  const classes = classList.toLowerCase().split(/\s+/);
  for (const cls of classes) {
    const matched = matchCategoryFromKeyword(cls);
    if (matched) return matched;
  }
  return null;
}

function normalizeText(str) {
  if (!str) return '';
  return String(str)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/[.,/#!$%^&*;:{}=\-_`~()?"'«»]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Detects slide category from Lyric JSON node, Lyric HTML metadata, or raw text.
 * Inspects structured attributes (data-type, custom-blockquote, sectionType, etc.)
 * as well as plain text lyric section markers.
 *
 * @param {string|object} input - Raw HTML string, LyricNode object, Lyric JSON string, or slide object
 * @returns {string|null} Detected category ID or null
 */
export function detectSlideCategory(input) {
  if (!input) return null;

  // 1. Structured Lyric Node / Page Object / DOM Element
  if (typeof input === 'object') {
    if (input.category && getCategoryConfig(input.category)) {
      return input.category;
    }
    if (typeof input.getAttribute === 'function') {
      const dt = input.getAttribute('data-type') || input.getAttribute('data-custom-section') || input.getAttribute('type') || input.getAttribute('name');
      if (dt) {
        const matched = matchCategoryFromKeyword(dt);
        if (matched) return matched;
      }
    }
    const typeCandidate = input.type || input.sectionType || input.section || input.dataType || input.name || '';
    if (typeCandidate) {
      const matched = matchCategoryFromKeyword(String(typeCandidate));
      if (matched) return matched;
    }
    if (input.className) {
      const matched = matchCategoryFromClasses(input.className);
      if (matched) return matched;
    }
    if (input.attrs) {
      const typeMatch = String(input.attrs).match(/data-type=["']([^"']+)["']/i);
      if (typeMatch) {
        const matched = matchCategoryFromKeyword(typeMatch[1]);
        if (matched) return matched;
      }
    }
    if (input.content) {
      return detectSlideCategory(input.content);
    }
    return null;
  }

  // 2. String input (HTML, JSON, or Plain Text)
  if (typeof input !== 'string') return null;
  const raw = input.trim();
  if (!raw) return null;

  // JSON stringified Lyric Node check
  if (raw.startsWith('{') && raw.endsWith('}')) {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        const fromJson = detectSlideCategory(parsed);
        if (fromJson) return fromJson;
      }
    } catch {
      // Continue with HTML parsing
    }
  }

  // 3. HTML Attributes (data-type="chorus", data-custom-section="intro", class="custom-blockquote q-verse")
  const dataTypeMatch = raw.match(/data-type=["']([a-zA-Z0-9_\s-]+)["']/i);
  if (dataTypeMatch) {
    const matched = matchCategoryFromKeyword(dataTypeMatch[1]);
    if (matched) return matched;
  }

  const customSectionMatch = raw.match(/data-custom-section=["']([a-zA-Z0-9_-]+)["']/i);
  if (customSectionMatch) {
    const matched = matchCategoryFromKeyword(customSectionMatch[1]);
    if (matched) return matched;
  }

  const classMatch = raw.match(/class=["']([^"']+)["']/i);
  if (classMatch) {
    const matched = matchCategoryFromClasses(classMatch[1]);
    if (matched) return matched;
  }

  // 4. Plain text regex inspection (e.g. [Refrão], [Verso 1], [Chorus], etc.)
  const clean = raw.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').trim();
  const firstLine = clean.split('\n')[0]?.trim() || '';

  for (const cat of SLIDE_CATEGORIES) {
    for (const pattern of cat.patterns) {
      if (pattern.test(firstLine) || pattern.test(clean)) {
        return cat.id;
      }
    }
  }

  return null;
}

/**
 * Extracts structured sections with lyrics from song's HTML/JSON chord sheet.
 * Returns array of { category, text, lines }
 */
export function extractSongSectionsFromLyric(lyricHtmlOrJson) {
  if (!lyricHtmlOrJson) return [];
  const sections = [];

  // String HTML or JSON
  if (typeof lyricHtmlOrJson === 'string') {
    const raw = lyricHtmlOrJson.trim();
    if (!raw) return [];

    if (raw.startsWith('{') || raw.startsWith('[')) {
      try {
        const parsed = JSON.parse(raw);
        return extractSongSectionsFromLyric(parsed);
      } catch {
        // Continue with HTML parsing
      }
    }

    if (typeof DOMParser !== 'undefined') {
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(raw, 'text/html');
        const sectionElements = doc.querySelectorAll('blockquote, .custom-blockquote, [data-custom-section], [data-type]');

        if (sectionElements.length > 0) {
          sectionElements.forEach((el) => {
            const cat = detectSlideCategory(el);
            if (cat) {
              const clone = el.cloneNode(true);
              clone.querySelectorAll('sup, .chord-sup, .chord-item, .chord-container, .chord-indicator').forEach((c) => c.remove());
              const text = (clone.textContent || '').replace(/&nbsp;/g, ' ').trim();
              const lines = text.split('\n').map((l) => l.trim()).filter((l) => l.length > 0);
              if (lines.length > 0) {
                sections.push({ category: cat, text, lines });
              }
            }
          });
        }
      } catch (err) {
        console.debug('[slideCategories] Error extracting sections from HTML:', err);
      }
    }
  } else if (typeof lyricHtmlOrJson === 'object' && lyricHtmlOrJson !== null) {
    const walk = (node) => {
      if (!node) return;
      if (node.className && (node.className.includes('custom-blockquote') || node.className.includes('q-'))) {
        const cat = detectSlideCategory(node);
        if (cat) {
          const text = (node.content || '').replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').trim();
          sections.push({ category: cat, text, lines: text.split('\n').map((l) => l.trim()).filter((l) => l.length > 0) });
        }
      }
      if (Array.isArray(node.children)) {
        node.children.forEach(walk);
      }
    };
    if (Array.isArray(lyricHtmlOrJson)) {
      lyricHtmlOrJson.forEach(walk);
    } else {
      walk(lyricHtmlOrJson);
    }
  }

  return sections;
}

/**
 * Matches a slide's text content against the song's extracted sections to determine its category.
 */
export function matchSlideCategoryFromSongSections(slideContent, songSections = []) {
  if (!slideContent || !Array.isArray(songSections) || songSections.length === 0) return null;

  const rawText = typeof slideContent === 'object'
    ? (slideContent.content || (Array.isArray(slideContent.layers) ? slideContent.layers.map((l) => l.html || '').join(' ') : ''))
    : String(slideContent);

  const cleanSlide = normalizeText(rawText);
  if (!cleanSlide) return null;

  // Extract key words (> 2 chars)
  const slideWords = cleanSlide.split(' ').filter((w) => w.length > 2);
  if (slideWords.length === 0) return null;

  let bestMatch = null;
  let highestScore = 0;

  for (const sec of songSections) {
    const cleanSection = normalizeText(sec.text);

    // Check direct substring inclusion
    if (cleanSection.includes(cleanSlide) || cleanSlide.includes(cleanSection)) {
      return sec.category;
    }

    // Check word overlap score
    const matchCount = slideWords.filter((w) => cleanSection.includes(w)).length;
    const score = matchCount / slideWords.length;

    if (score >= 0.4 && score > highestScore) {
      highestScore = score;
      bestMatch = sec.category;
    }
  }

  return bestMatch;
}

/**
 * Finds next slide index in current song matching the requested category
 * If after the last match, loops back to the first match
 */
export function findNextSlideByCategory(pages = [], currentIndex = 0, categoryId = '') {
  if (!Array.isArray(pages) || pages.length === 0 || !categoryId) return null;

  const matchingIndices = [];
  pages.forEach((p, idx) => {
    if (p && !p.skipped && p.category === categoryId) {
      matchingIndices.push(idx);
    }
  });

  if (matchingIndices.length === 0) return null;

  // Find the next index strictly greater than currentIndex
  const next = matchingIndices.find((idx) => idx > currentIndex);
  if (next !== undefined) return next;

  // Loop back to the first match
  return matchingIndices[0];
}

/**
 * Collects distinct categories present in the given pages list,
 * preserving chronological order of their first appearance.
 */
export function getSongCategories(pages = []) {
  if (!Array.isArray(pages)) return [];
  const foundIds = new Set();
  const result = [];

  pages.forEach((p, idx) => {
    if (p && p.category && !foundIds.has(p.category) && p.id !== '_blank') {
      foundIds.add(p.category);
      const cfg = getCategoryConfig(p.category);
      if (cfg) {
        const count = pages.filter((item) => item && item.category === p.category).length;
        result.push({
          ...cfg,
          firstSlideIndex: idx,
          count,
        });
      }
    }
  });

  return result;
}
